import { useState, useEffect } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";

export default function MaterialLedger() {
  const [inventory, setInventory] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);

  const [tab, setTab] = useState("inward");
  const [showModal, setShowModal] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [branches, setBranches] = useState([]);

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);

  const rowsPerPage = 10;

  const today = () => new Date().toISOString().split("T")[0];

  const getEmptyRow = () =>
    tab === "inward"
      ? {
          date: today(),
          poId: "",
          item: "",
          qty: "",
          unit: "Kg",
          quality: "pass",
          notes: "",
          branch_id: "",
          vendor: "", // 🔥 ADD THIS
          target_branch_id: "", // 🔥 ADD THIS (safe)
        }
      : {
          date: today(),
          item: "",
          qty: "",
          unit: "Kg",
          purpose: "",
          branch_id: "",
          target_branch_id: "", // 🔥 ADD THIS
        };

  const [rows, setRows] = useState([getEmptyRow()]);

  // ================= FETCH =================
  useEffect(() => {
    fetchInventory();
    fetchPOs();
    fetchLedger();
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const res = await fetch("/api/branches", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setBranches(data.data);
      } else {
        console.log("Branch fetch failed:", data.message);
      }
    } catch (err) {
      console.error("Branch error:", err);
    }
  };

  const fetchLedger = async () => {
    const res = await fetch("/api/inventory/ledger", {
      credentials: "include",
    });
    const data = await res.json();
    if (data.status === "success") setLedger(data.data);
  };

  const fetchInventory = async () => {
    const res = await fetch("/api/inventory/items", {
      credentials: "include",
    });
    const data = await res.json();
    if (data.status === "success") setInventory(data.data);
  };

  const fetchPOs = async () => {
    // ✅ FIXED ENDPOINT
    const res = await fetch("/api/inventory/po", {
      credentials: "include",
    });
    const data = await res.json();
    if (data.status === "success") setPurchaseOrders(data.data);
  };

  // ================= HELPERS =================
  const getPOItems = (poId) => {
    const po = purchaseOrders.find((p) => p.id === Number(poId));
    return po?.items || [];
  };

  const getItemId = (name) => {
    return inventory.find((i) => i.name === name)?.id;
  };

  // ================= ROW =================
  const addRow = () => setRows([...rows, getEmptyRow()]);

  const updateRow = (i, field, val) => {
    const updated = [...rows];
    updated[i][field] = val;

    // 🔥 PO → auto-fill first item
    if (field === "poId") {
      const po = purchaseOrders.find((p) => p.id === Number(val));

      if (po) {
        updated[i].vendor = po.vendors?.join(", ");

        const firstItem = po.items?.[0];
        if (firstItem) {
          updated[i].item = firstItem.name;
          updated[i].unit = firstItem.unit;
          updated[i].qty = firstItem.qty;
        }
      }
    }

    // 🔥 ITEM → update unit
    if (field === "item") {
      const poItems = getPOItems(updated[i].poId);
      const selected = poItems.find((it) => it.name === val);

      if (selected) {
        updated[i].unit = selected.unit;
      }
    }

    setRows(updated);
  };

  const removeRow = (i) => {
    setRows(rows.filter((_, idx) => idx !== i));
  };

  const filteredLedger = ledger.filter((row) => {
    if (row.type !== tab) return false;

    const searchText = search.trim().toLowerCase();

    const matchesSearch =
      !searchText ||
      row.item?.toLowerCase().includes(searchText) ||
      row.branch?.toLowerCase().includes(searchText) ||
      row.reference?.toLowerCase().includes(searchText) ||
      row.notes?.toLowerCase().includes(searchText) ||
      row.edited_by_name?.toLowerCase().includes(searchText) ||
      row.edited_by_employee_id?.toLowerCase().includes(searchText);

    const ledgerDate = row.date ? String(row.date).slice(0, 10) : "";

    const matchesFromDate = !fromDate || ledgerDate >= fromDate;
    const matchesToDate = !toDate || ledgerDate <= toDate;

    return matchesSearch && matchesFromDate && matchesToDate;
  });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredLedger.length / rowsPerPage),
  );

  const currentPage = Math.min(page, totalPages);

  const paginatedLedger = filteredLedger.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  useEffect(() => {
    setPage(1);
  }, [tab, search, fromDate, toDate]);

  const getTypeStyle = (type) => {
    if (type === "inward") {
      return "bg-green-500/20 text-green-400";
    }

    if (type === "outward") {
      return "bg-red-500/20 text-red-400";
    }

    return "bg-amber-400/20 text-amber-300";
  };

  const getQuantityStyle = (type, qty) => {
    if (type === "inward") return "text-green-400";
    if (type === "outward") return "text-red-400";

    return Number(qty) >= 0 ? "text-emerald-400" : "text-red-400";
  };

  const getQuantityPrefix = (type, qty) => {
    if (type === "inward") return "+";
    if (type === "outward") return "-";

    return Number(qty) > 0 ? "+" : "";
  };

  // ================= SAVE =================
  const saveEntries = async () => {
    try {
      for (let r of rows) {
        if (!r.item || !r.qty) {
          alert("Item and quantity required");
          return;
        }
      }

      // ================= INWARD =================
      if (tab === "inward") {
        const payload = {
          entries: rows.map((r) => ({
            item_id: getItemId(r.item), // existing item
            item_name: r.item, // 👈 ADD (for auto-create)
            qty: Number(r.qty),
            unit: r.unit,

            po_id: r.poId || null,

            // 👇 IMPORTANT
            source_type: r.poId ? "po" : "direct",

            // 👇 IMPORTANT
            reference: r.poId ? `PO-${r.poId}` : r.notes || "manual entry",

            branch_id: r.branch_id || null,
          })),
        };

        const res = await fetch("/api/inventory/inward", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        if (data.status !== "success") {
          alert(data.message);
          return;
        }
      }

      // ================= OUTWARD =================
      if (tab === "outward") {
        const payload = {
          method: "FIFO",
          entries: rows.map((r) => ({
            item_id: getItemId(r.item),
            qty: Number(r.qty),

            reference: r.purpose || "consumed",

            branch_id: r.branch_id || null,

            // 👇 ADD THIS
            target_branch_id: r.target_branch_id || null,
          })),
        };

        const res = await fetch("/api/inventory/outward", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        if (data.status !== "success") {
          alert(data.message);
          return;
        }
      }

      alert("Saved successfully ✅");
      setShowModal(false);
      setRows([getEmptyRow()]);
      fetchInventory();
      fetchLedger();
    } catch (err) {
      console.error(err);
      alert("Failed to save");
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* HEADER */}
        <div className="flex justify-between">
          <h1 className="text-2xl font-serif">
            {tab === "adjustment"
              ? "Inventory Edit History"
              : "Material Ledger"}
          </h1>

          {tab !== "adjustment" && (
            <button
              onClick={() => {
                setRows([getEmptyRow()]);
                setShowModal(true);
              }}
              className="px-5 py-2 rounded-xl bg-amber-400 text-black"
            >
              + Add {tab === "inward" ? "Inward" : "Outward"}
            </button>
          )}
        </div>

        {/* TABS */}
        <div className="flex gap-2 bg-black/40 p-1 rounded-xl w-fit">
          <button
            onClick={() => setTab("inward")}
            className={`px-5 py-2 rounded-lg ${
              tab === "inward" ? "bg-amber-400 text-black" : "text-gray-400"
            }`}
          >
            Inward
          </button>

          <button
            onClick={() => setTab("outward")}
            className={`px-5 py-2 rounded-lg ${
              tab === "outward" ? "bg-amber-400 text-black" : "text-gray-400"
            }`}
          >
            Outward
          </button>

          <button
            onClick={() => setTab("adjustment")}
            className={`px-5 py-2 rounded-lg ${
              tab === "adjustment" ? "bg-amber-400 text-black" : "text-gray-400"
            }`}
          >
            Edit History
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 md:grid-cols-4">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search item, editor, code, branch..."
            className="rounded-lg border border-white/10 bg-black/40 px-4 py-2 md:col-span-2"
          />

          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
          />

          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
          />

          <button
            onClick={() => {
              setSearch("");
              setFromDate("");
              setToDate("");
            }}
            className="rounded-lg border border-white/20 px-4 py-2 text-sm text-gray-300 hover:bg-white/5 md:col-start-4"
          >
            Clear Filters
          </button>
        </div>

        {/* ================= LEDGER TABLE ================= */}
        <div className="bg-black/40 rounded-2xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/60 text-gray-400">
                <tr>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Item</th>
                  <th className="p-3 text-left">Type</th>
                  <th className="p-3 text-center">Qty</th>
                  <th className="p-3 text-left">Edited By</th>
                  <th className="p-3 text-left">Employee Code</th>
                  <th className="p-3 text-left">Reason / Details</th>
                  <th className="p-3 text-left">Branch</th>
                </tr>
              </thead>

              <tbody>
                {paginatedLedger.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="p-6 text-center text-gray-500">
                      No{" "}
                      {tab === "adjustment"
                        ? "inventory edit history"
                        : "ledger entries"}{" "}
                      found.
                    </td>
                  </tr>
                ) : (
                  paginatedLedger.map((row) => (
                    <tr
                      key={row.id}
                      className="border-t border-white/5 hover:bg-white/5"
                    >
                      <td className="p-3 whitespace-nowrap text-gray-400">
                        {new Date(row.date).toLocaleString("en-IN", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </td>

                      <td className="p-3 font-medium">{row.item}</td>

                      <td className="p-3">
                        <span
                          className={`rounded px-2 py-1 text-xs ${getTypeStyle(
                            row.type,
                          )}`}
                        >
                          {row.type === "adjustment" ? "Edited" : row.type}
                        </span>
                      </td>

                      <td
                        className={`p-3 text-center font-medium ${getQuantityStyle(
                          row.type,
                          row.qty,
                        )}`}
                      >
                        {getQuantityPrefix(row.type, row.qty)} {row.qty}{" "}
                        {row.unit || ""}
                      </td>

                      <td className="p-3">{row.edited_by_name || "System"}</td>

                      <td className="p-3">
                        {row.edited_by_employee_id || "-"}
                      </td>

                      <td className="max-w-md p-3 text-xs text-gray-400">
                        {row.notes || row.reference || "—"}
                      </td>

                      <td className="p-3 text-gray-400">{row.branch || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-col gap-3 rounded-b-2xl border-x border-b border-white/10 bg-black/20 px-4 py-3 text-sm md:flex-row md:items-center md:justify-between">
          <p className="text-gray-400">
            Showing{" "}
            {filteredLedger.length === 0
              ? 0
              : (currentPage - 1) * rowsPerPage + 1}
            {" - "}
            {Math.min(currentPage * rowsPerPage, filteredLedger.length)} of{" "}
            {filteredLedger.length} entries
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((previous) => Math.max(1, previous - 1))}
              disabled={currentPage === 1}
              className="rounded-lg border border-white/15 px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>

            <span className="px-2 text-gray-300">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() =>
                setPage((previous) => Math.min(totalPages, previous + 1))
              }
              disabled={currentPage === totalPages}
              className="rounded-lg border border-white/15 px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>

        {/* MODAL */}
        {showModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="w-full max-w-7xl bg-[#0b0b0f] p-8 rounded-2xl border border-white/10">
              <div className="flex justify-between mb-4">
                <h2 className="font-serif text-xl">
                  Add {tab === "inward" ? "Inward" : "Outward"}
                </h2>
              </div>

              {rows.map((row, i) => (
                <div key={i} className="grid grid-cols-8 gap-3 mb-3">
                  <input
                    type="date"
                    value={row.date}
                    onChange={(e) => updateRow(i, "date", e.target.value)}
                    className="bg-black/40 p-2 rounded"
                  />

                  {tab === "inward" && (
                    <>
                      <select
                        value={row.poId}
                        onChange={(e) => updateRow(i, "poId", e.target.value)}
                        className="bg-black/40 p-2 rounded"
                      >
                        <option value="">Select PO</option>
                        {purchaseOrders.map((po) => (
                          <option key={po.id} value={po.id}>
                            {po.po_number} ({po.vendors.join(", ")})
                          </option>
                        ))}
                      </select>

                      <input
                        value={row.vendor}
                        readOnly
                        placeholder="Vendor auto-filled"
                        className="bg-black/20 p-2 rounded text-gray-400"
                      />
                    </>
                  )}

                  {tab === "inward" && row.poId ? (
                    // ✅ PO SELECTED → SHOW PO ITEMS
                    <select
                      value={row.item}
                      onChange={(e) => updateRow(i, "item", e.target.value)}
                      className="bg-black/40 p-2 rounded"
                    >
                      <option value="">Select PO Item</option>
                      {getPOItems(row.poId).map((it, idx) => (
                        <option key={idx} value={it.name}>
                          {it.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    // ✅ NO PO → SELECT EXISTING ITEMS
                    <select
                      value={row.item}
                      onChange={(e) => updateRow(i, "item", e.target.value)}
                      className="bg-black/40 p-2 rounded"
                    >
                      <option value="">Select Item</option>
                      {inventory.map((it) => (
                        <option key={it.id} value={it.name}>
                          {it.name}
                        </option>
                      ))}
                    </select>
                  )}

                  <input
                    type="number"
                    placeholder="Qty"
                    value={row.qty}
                    onChange={(e) => updateRow(i, "qty", e.target.value)}
                    className="bg-black/40 p-2 rounded"
                  />

                  <select
                    value={row.unit}
                    onChange={(e) => updateRow(i, "unit", e.target.value)}
                    className="bg-black/40 p-2 rounded"
                  >
                    <option>Kg</option>
                    <option>Litres</option>
                    <option>Pieces</option>
                  </select>

                  <select
                    value={row.branch_id}
                    onChange={(e) => updateRow(i, "branch_id", e.target.value)}
                    className="bg-black/40 p-2 rounded"
                  >
                    <option value="">Select Branch</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>

                  {tab === "outward" && (
                    <select
                      value={row.target_branch_id}
                      onChange={(e) =>
                        updateRow(i, "target_branch_id", e.target.value)
                      }
                      className="bg-black/40 p-2 rounded"
                    >
                      <option value="">Transfer To (optional)</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  )}

                  {tab === "inward" ? (
                    <>
                      <select
                        value={row.quality}
                        onChange={(e) =>
                          updateRow(i, "quality", e.target.value)
                        }
                        className="bg-black/40 p-2 rounded"
                      >
                        <option value="pass">Quality OK</option>
                        <option value="conditional">Conditional</option>
                        <option value="fail">Rejected</option>
                      </select>

                      <input
                        placeholder="QC Notes"
                        value={row.notes}
                        onChange={(e) => updateRow(i, "notes", e.target.value)}
                        className="bg-black/40 p-2 rounded"
                      />

                      <input
                        type="file"
                        accept="video/*"
                        onChange={(e) =>
                          updateRow(i, "video", e.target.files[0])
                        }
                        className="bg-black/40 p-2 rounded"
                      />
                    </>
                  ) : (
                    <input
                      placeholder="Purpose"
                      value={row.purpose}
                      onChange={(e) => updateRow(i, "purpose", e.target.value)}
                      className="bg-black/40 p-2 rounded"
                    />
                  )}

                  <button onClick={() => removeRow(i)} className="text-red-400">
                    Remove
                  </button>
                </div>
              ))}

              <div className="flex justify-between mt-6">
                <button onClick={addRow}>+ Add Row</button>
                <div className="flex gap-3">
                  <button onClick={() => setShowModal(false)}>Cancel</button>
                  <button
                    onClick={saveEntries}
                    className="bg-amber-400 text-black px-4 py-2 rounded"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
